<script>
  import { Contact, MessageCircle, PanelLeftClose, PanelLeftOpen, Settings, UserPlus, Users } from 'lucide-svelte';
  import Popup from '@components/messenger/chat/mobile/Popup.svelte';
  import AddUser from '@components/messenger/chat/mobile/AddUser.svelte';
  import AddGroup from '@components/messenger/chat/mobile/AddGroup.svelte';
  import SettingsPanel from '@components/messenger/chat/mobile/SettingsPanel.svelte';
  import ContactPanel from '@components/messenger/chat/mobile/ContactPanel.svelte';
  import ConversationItem from '@components/messenger/conversations/ConversationItem.svelte';
  import ConversationSearch from '@components/messenger/searchs/conversations/ConversationSearch.svelte';
  import { filterConversations } from '@components/messenger/searchs/conversations/filter-conversations.js';
  import { conversations, conversationId, loadMessagesWS, messages, status } from '$lib/websocket-client.js';
  import { faceStore } from '$lib/stores/face-store';

  const { menuVisible, toggleMenu } = faceStore;

  export let openChat;

  let searchKeyword = '';
  let showSearch = true;
  let lastScrollTop = 0;
  let showPopup = false;
  let popupType = null;
  let popupMode = 'bottom';
  let popupRect = null;

  function getConnectionState(value = '') {
    const raw = String(value).toLowerCase();

    if (raw.includes('disconnected') || raw.includes('ws closed')) {
      return {
        label: 'Đã ngắt kết nối',
        tone: 'text-slate-500',
        dot: 'bg-slate-400'
      };
    }

    if (raw.includes('reconnect') || raw.includes('retry')) {
      return {
        label: 'Đang kết nối lại My Cloud',
        tone: 'text-rose-500',
        dot: 'bg-rose-500'
      };
    }

    if (raw.includes('connected')) {
      return {
        label: 'Đã kết nối My Cloud',
        tone: 'text-emerald-600',
        dot: 'bg-emerald-500'
      };
    }

    if (raw.includes('connecting')) {
      return {
        label: 'Đang khởi tạo kết nối',
        tone: 'text-amber-600',
        dot: 'bg-amber-500'
      };
    }

    return {
      label: 'Đang kiểm tra kết nối',
      tone: 'text-slate-500',
      dot: 'bg-slate-400'
    };
  }

  $: connectionState = getConnectionState($status);

  $: filteredConversations = filterConversations($conversations || [], searchKeyword)
    .sort((a, b) => {
      if (a.type === 'cloud') return -1;
      if (b.type === 'cloud') return 1;
      return new Date(b.lastMessageAt || b.createdAt || 0) - new Date(a.lastMessageAt || a.createdAt || 0);
    });

  async function openConversation(conversation) {
    if (!conversation?.conversationId) return;

    conversationId.set(conversation.conversationId);
    messages.set([]);
    openChat?.(conversation);
    await loadMessagesWS(conversation.conversationId);
  }

  function handleScroll(event) {
    const current = event.currentTarget.scrollTop;
    showSearch = current < lastScrollTop || current < 20;
    lastScrollTop = current;
  }

  function openPopup({ type, mode = 'bottom', event = null }) {
    popupType = type;
    popupMode = mode;
    popupRect = mode === 'anchor' && event?.currentTarget ? event.currentTarget.getBoundingClientRect() : null;
    showPopup = true;
  }

  function closePopup() {
    showPopup = false;
    popupType = null;
    popupRect = null;
  }
</script>

<div class="conversation-screen">
  <header class="list-header">
    <div class="flex items-start justify-between gap-3">
      <div>
        <h1 class="text-xl font-semibold tracking-tight text-slate-900">Tin nhắn</h1>
        <p class={`mt-0.5 flex items-center gap-1.5 text-[11px] ${connectionState.tone}`}>
          <span class={`h-2 w-2 rounded-full ${connectionState.dot}`}></span>
          <span>{connectionState.label}</span>
        </p>
      </div>

      <div class="flex items-center gap-1.5">
        <button class="action-button" aria-label="Thêm bạn" onclick={(event) => openPopup({ type: 'user', mode: 'bottom', event })}>
          <UserPlus size={18} />
        </button>
        <button class="action-button" aria-label="Tạo nhóm" onclick={(event) => openPopup({ type: 'group', mode: 'bottom', event })}>
          <Users size={18} />
        </button>
        <button class="action-button" aria-label="Mở menu" onclick={toggleMenu}>
          {#if $menuVisible}<PanelLeftClose size={18} />{:else}<PanelLeftOpen size={18} />{/if}
        </button>
      </div>
    </div>

    <div class:search-hidden={!showSearch} class="search-wrap">
      <ConversationSearch placeholder="Tìm cuộc trò chuyện" debounce={180} onSearch={(value) => (searchKeyword = value)} />
    </div>
  </header>

  <main class="conversation-list" onscroll={handleScroll}>
    {#each filteredConversations as conversation (conversation.conversationId)}
      <ConversationItem
        {conversation}
        selectedId={$conversationId}
        onSelect={openConversation}
        className="mx-2 mb-1 touch-manipulation" />
    {:else}
      <div class="empty-list">Không tìm thấy cuộc trò chuyện phù hợp.</div>
    {/each}
  </main>

  <nav class="bottom-nav">
    <button class="nav-item active"><MessageCircle size={21} /><span>Tin nhắn</span></button>
    <button class="nav-item" onclick={(event) => openPopup({ type: 'contacts', mode: 'center', event })}>
      <Contact size={21} /><span>Danh bạ</span>
    </button>
    <button class="nav-item" onclick={(event) => openPopup({ type: 'settings', mode: 'bottom', event })}>
      <Settings size={21} /><span>Cài đặt</span>
    </button>
  </nav>
</div>

<Popup bind:open={showPopup} mode={popupMode} anchorRect={popupRect}>
  {#if popupType === 'user'}
    <AddUser onClose={closePopup} />
  {:else if popupType === 'group'}
    <AddGroup onClose={closePopup} />
  {:else if popupType === 'settings'}
    <SettingsPanel onClose={closePopup} />
  {:else if popupType === 'contacts'}
    <ContactPanel onClose={closePopup} />
  {/if}
</Popup>

<style>
  .conversation-screen {
    display: flex;
    flex-direction: column;
    width: 100%;
    height: 100dvh;
    overflow: hidden;
    background:
      radial-gradient(circle at top right, rgba(96, 165, 250, 0.16), transparent 20rem),
      #f8fafc;
  }

  .list-header {
    z-index: 10;
    padding: max(1rem, env(safe-area-inset-top)) 1rem 0.75rem;
    border-bottom: 1px solid rgba(226, 232, 240, 0.7);
    background: rgba(255, 255, 255, 0.82);
    backdrop-filter: blur(22px);
  }

  .action-button {
    display: grid;
    place-items: center;
    width: 2.35rem;
    height: 2.35rem;
    border: 1px solid rgba(148, 163, 184, 0.2);
    border-radius: 999px;
    color: #334155;
    background: rgba(255, 255, 255, 0.72);
  }

  .search-wrap {
    max-height: 3rem;
    margin-top: 0.8rem;
    opacity: 1;
    transition: max-height 180ms ease, margin 180ms ease, opacity 150ms ease;
  }

  .search-wrap.search-hidden {
    max-height: 0;
    margin-top: 0;
    overflow: hidden;
    opacity: 0;
  }

  .conversation-list {
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    overscroll-behavior: contain;
    padding: 0.5rem 0;
    -webkit-overflow-scrolling: touch;
  }

  .empty-list {
    padding: 3rem 1rem;
    color: #64748b;
    text-align: center;
    font-size: 0.85rem;
  }

  .bottom-nav {
    z-index: 10;
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    padding: 0.45rem 0.5rem max(0.45rem, env(safe-area-inset-bottom));
    border-top: 1px solid rgba(226, 232, 240, 0.72);
    background: rgba(255, 255, 255, 0.86);
    backdrop-filter: blur(22px);
  }

  .nav-item {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.15rem;
    padding: 0.25rem;
    color: #64748b;
    font-size: 0.65rem;
  }

  .nav-item.active {
    color: #2563eb;
  }
</style>
